import {captureMessage} from '@sentry/react';
import React from 'react';
import {useQueryParam} from 'use-query-params';

import AggOptions from 'src/components/AggOptions';
import {recordEvent} from 'src/components/Analytics';
import ForecastSelect, {Shortcut, getForecastTimesForDays} from 'src/components/ForecastSelect';
import {H5FlowModel, makeH5FlowVizConfig} from 'src/components/h5/flowVizConfig';
import H5FlowLegend, {H5LegendModel, RemoveModelButton} from 'src/components/h5/H5FlowLegend';
import RequestFailed from 'src/components/h5/RequestFailed';
import {useAggregation} from 'src/components/h5/useAggregation';
import WeatherCharts from 'src/components/h5/WeatherCharts';
import LoadingSpinner from 'src/components/LoadingSpinner';
import {ThresholdLabelOverlays, ThresholdLines} from 'src/components/ThresholdLines';
import TimeSeriesViz, {
  SOLO_VIZ_HEIGHT,
  SOLO_VIZ_HEIGHT_WITH_LEGEND,
} from 'src/components/TimeSeriesViz';
import {Translator, useTranslations} from 'src/components/TranslationsContext';
import Viz from 'src/components/Viz';
import WithWidth from 'src/components/WithWidth';
import {useUnitSystem} from 'src/contexts/UnitSystemContext';
import {useH5ForecastData, useH5ForecastListing} from 'src/hooks/h5/useH5Forecasts';
import {useH5ObservationData} from 'src/hooks/h5/useH5Observations';
import {useForecastTimeSelection} from 'src/hooks/useForecastTimeSelection';
import {useThresholdLinesForSite} from 'src/hooks/useThresholdLinesForSite';
import {ApiUserProfile} from 'src/lib/api/auth';
import {
  H5ForecastDataRequest,
  H5ForecastListing,
  H5ObservationDataRequest,
  H5_MAX_SELECTED_FORECASTS,
  UnsupportedH5SchemaError,
  defaultListing,
  forecastListingsForSection,
  listingKey,
} from 'src/lib/api/h5';
import {Project, Site} from 'src/lib/api/project';
import {
  DashboardSection,
  Source,
  TimeAggregation,
  isCumulativeMode,
  modelSource,
} from 'src/lib/api/series';
import {addDays, formatDateMonthDayTime, makeDateRangeQueryParam, now} from 'src/lib/dates';
import {forecastTimesWithoutData, formatForecastTime} from 'src/lib/h5/forecastTimes';
import {forecastHorizonDays} from 'src/lib/h5/horizon';
import {modelDisplayNames} from 'src/lib/h5/modelNames';
import {pairModelRuns} from 'src/lib/h5/modelRuns';
import {flowColumnFor, toForecastTimeSeries} from 'src/lib/h5/toForecastTimeSeries';
import {toObservationTimeSeries} from 'src/lib/h5/toObservationTimeSeries';
import {getDefaultRateVolumeMode} from 'src/lib/siteUtils';
import * as tw from 'src/lib/tailwindClasses';
import {isUpstreamInternalOrgUser} from 'src/lib/userUtils';

type SectionSettings = {
  // The chart's styling identity for the series; the legacy component for the same section uses
  // the same one, which is what keeps the two looking alike.
  source: Source;
  // The legacy section's date param, so links made before H5 keep their range. Site.tsx overrides
  // it when the legacy section is still shown alongside.
  dateRangeParam: string;
  // Only the short-term section takes the project's configured default range; seasonal and annual
  // defaults come from their own spans.
  usesProjectDefaultDateRange: boolean;
  defaultTimeAggregation: TimeAggregation;
  // How far the default window reaches forward and back from today. Needed before any listing
  // has loaded, so these are the section's usual spans rather than a model's own horizon.
  // TODO(h5): once the listing carries each model's horizon, widen the window to it.
  defaultHorizonDays: number;
  defaultHistoryDays: number;
  analyticsSection: string;
  shortcuts: (
    t: Translator,
    defaultDateRange: [Date, Date],
    defaultHorizonDays: number
  ) => Shortcut[];
};

const SECTION_SETTINGS: Record<DashboardSection, SectionSettings> = {
  [DashboardSection.ShortTerm]: {
    source: Source.HydroForecastShortTerm,
    dateRangeParam: 'stDateRange',
    usesProjectDefaultDateRange: true,
    defaultTimeAggregation: '1H',
    defaultHorizonDays: 10,
    defaultHistoryDays: 1,
    analyticsSection: 'short_term',
    shortcuts: (t, defaultDateRange) => [
      {displayName: t('latest'), getSelection: (times) => (times[0] ? [times[0]] : [])},
      {
        displayName: t('last3Days'),
        getSelection: (times) => getForecastTimesForDays(times, 3),
        startDate: addDays(defaultDateRange[0], -3),
        endDate: defaultDateRange[1],
      },
      {
        displayName: t('last10Days'),
        getSelection: (times) => getForecastTimesForDays(times, 10),
        startDate: addDays(defaultDateRange[0], -10),
        endDate: defaultDateRange[1],
      },
    ],
  },
  [DashboardSection.Seasonal]: {
    source: Source.HydroForecastSeasonal,
    dateRangeParam: 'seasonalDateRange',
    usesProjectDefaultDateRange: false,
    defaultTimeAggregation: '1D',
    defaultHorizonDays: 90,
    defaultHistoryDays: 91,
    analyticsSection: 'seasonal',
    shortcuts: (t, _defaultDateRange, defaultHorizonDays) => [
      {displayName: t('latest'), getSelection: (times) => (times[0] ? [times[0]] : [])},
      {
        displayName: t('latestYTD'),
        getSelection: (times) => (times[0] ? [times[0]] : []),
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: addDays(new Date(), defaultHorizonDays),
      },
    ],
  },
  [DashboardSection.Annual]: {
    source: Source.HydroForecastSeasonal,
    dateRangeParam: 'annualDateRange',
    usesProjectDefaultDateRange: false,
    defaultTimeAggregation: '1D',
    defaultHorizonDays: 370,
    defaultHistoryDays: 181,
    analyticsSection: 'annual',
    shortcuts: (t) => [
      {displayName: t('latest'), getSelection: (times) => (times[0] ? [times[0]] : [])},
    ],
  },
};

type AvailableListing = Extract<H5ForecastListing, {status: 'available'}>;

const isAvailableListing = (listing: H5ForecastListing): listing is AvailableListing =>
  listing.status === 'available';

// The same array while its items are, so a list rebuilt each render can be a memo dependency.
// The ref is read during render on purpose: the result depends only on the lists rendered so far,
// and holding it in state would render the section again whenever the list changed.
function useShallowStable<T>(items: T[]): T[] {
  /* eslint-disable react-hooks/refs */
  const stable = React.useRef(items);
  const changed =
    items.length !== stable.current.length ||
    items.some((item, index) => item !== stable.current[index]);
  if (changed) stable.current = items;
  return stable.current;
  /* eslint-enable react-hooks/refs */
}

const initTimesOf = (forecasts: {initializationTime: Date}[], locale: string, timeZone: string) =>
  forecasts
    .map((forecast) => formatDateMonthDayTime(forecast.initializationTime, locale, timeZone))
    .join(', ');

/**
 * A dashboard section drawn from H5: the section's forecast picker, one flow chart of the selected
 * models with the controls the legacy section has, and the section's weather under it. Every read
 * goes to the H5 routes.
 */
export default function FlowSection({
  project,
  site,
  section,
  dateRangeParam,
  currentUser,
}: {
  project: Project;
  site: Site;
  section: DashboardSection;
  dateRangeParam?: string;
  currentUser?: ApiUserProfile | null;
}) {
  const {t, locale} = useTranslations();
  const unitSystem = useUnitSystem();
  const settings = SECTION_SETTINGS[section];
  const {defaultHorizonDays, defaultHistoryDays} = settings;
  const projectDefaultDateRange = settings.usesProjectDefaultDateRange
    ? project.defaultDateRange
    : undefined;
  const defaultDateRange = React.useMemo(
    () =>
      projectDefaultDateRange ?? [
        now(site.localTimeZone).minus({days: defaultHistoryDays}).startOf('day').toJSDate(),
        now(site.localTimeZone).plus({days: defaultHorizonDays}).endOf('day').toJSDate(),
      ],
    [projectDefaultDateRange, site.localTimeZone, defaultHistoryDays, defaultHorizonDays]
  ) as [Date, Date];
  const DateRangeQueryParam = React.useMemo(
    () => makeDateRangeQueryParam(defaultDateRange),
    [defaultDateRange]
  );
  const [dateRange, setDateRange] = useQueryParam(
    dateRangeParam ?? settings.dateRangeParam,
    DateRangeQueryParam
  );
  const initTimeRange: [Date, Date] = [addDays(dateRange[0], -defaultHorizonDays), dateRange[1]];
  const listingQuery = useH5ForecastListing(site, initTimeRange);
  const sectionListings = React.useMemo(
    () => forecastListingsForSection(listingQuery.data, section),
    [listingQuery.data, section]
  );
  // Undefined until the user picks, so the section follows the default model as listings load. In
  // the order picked, so adding a model keeps the others' runs and colors.
  const [selectedListingKeys, setSelectedListingKeys] = React.useState<string[]>();
  const sectionDefaultListing = React.useMemo(
    () => defaultListing(sectionListings),
    [sectionListings]
  );
  // The default model first, so it keeps the HydroForecast colors and the section's source.
  const orderedListings = React.useMemo(
    () =>
      sectionDefaultListing
        ? [
            sectionDefaultListing,
            ...sectionListings.filter((listing) => listing !== sectionDefaultListing),
          ]
        : sectionListings,
    [sectionListings, sectionDefaultListing]
  );
  const modelNames = React.useMemo(() => {
    const names = modelDisplayNames(orderedListings);
    return new Map(orderedListings.map((listing, index) => [listingKey(listing), names[index]]));
  }, [orderedListings]);
  const nameOf = (listing: H5ForecastListing) => modelNames.get(listingKey(listing))!;
  const selectedListings = React.useMemo(() => {
    const defaultSelection = sectionDefaultListing ? [sectionDefaultListing] : [];
    if (!selectedListingKeys) return defaultSelection;
    const picked = selectedListingKeys.flatMap(
      (key) => orderedListings.find((listing) => listingKey(listing) === key) ?? []
    );
    // Picks the listing no longer has, as after a date range change, fall back to the default.
    if (picked.length === 0 && selectedListingKeys.length > 0) return defaultSelection;
    // The default first, so whenever it is drawn with runs it sets the runs to pick from.
    return sectionDefaultListing && picked.includes(sectionDefaultListing)
      ? [sectionDefaultListing, ...picked.filter((listing) => listing !== sectionDefaultListing)]
      : picked;
  }, [orderedListings, selectedListingKeys, sectionDefaultListing]);
  const addableListings = orderedListings.filter((listing) => !selectedListings.includes(listing));
  const selectModels = (listings: H5ForecastListing[]) => {
    setSelectedListingKeys(listings.map(listingKey));
    recordEvent('forecast_model_changed', {
      section: settings.analyticsSection,
      count: listings.length,
    });
  };
  const removeModel = (listing: H5ForecastListing) =>
    selectModels(selectedListings.filter((other) => other !== listing));
  // The default model stays: removing it would change which model the run picker lists runs from.
  const canRemove = React.useCallback(
    (listing: H5ForecastListing) => listing !== sectionDefaultListing,
    [sectionDefaultListing]
  );
  const removeButton = (listing: H5ForecastListing) =>
    canRemove(listing) ? <RemoveModelButton onClick={() => removeModel(listing)} /> : null;
  const showModelInfo = isUpstreamInternalOrgUser(currentUser ?? null);
  const availableListings = React.useMemo(
    () => selectedListings.filter(isAvailableListing),
    [selectedListings]
  );
  // The first model drawn with runs in the period sets the runs to pick from; the others are
  // compared against it. A model with no runs is still drawn, as having none to pair.
  const anchorListing =
    availableListings.find((listing) => listing.availableForecasts.length > 0) ??
    availableListings.at(0);
  const forecastTimes = anchorListing?.availableForecasts;
  const {selectedForecastTimes, setSelectedForecastTimes, onNextForecastTimesChange} =
    useForecastTimeSelection(
      forecastTimes,
      dateRange,
      anchorListing ? listingKey(anchorListing) : ''
    );

  const defaultRateVolumeMode = getDefaultRateVolumeMode(site);
  const {aggregation, updateAggregation} = useAggregation(dateRange, {
    timeAggregation: settings.defaultTimeAggregation,
    rateVolumeMode: defaultRateVolumeMode === 'level' ? 'rate' : defaultRateVolumeMode,
  });
  const {timeAggregation} = aggregation;
  const siteThresholdConfigs = useThresholdLinesForSite(
    site.id,
    currentUser?.id,
    aggregation.rateVolumeMode
  );
  // The configs are short-term forecast alerts, so only the short-term chart draws them.
  const thresholdConfigs = section === DashboardSection.ShortTerm ? siteThresholdConfigs : [];
  const [thresholdLinesVisible, setThresholdLinesVisible] = React.useState(true);
  const modelRuns = React.useMemo(
    () => pairModelRuns(availableListings, anchorListing, selectedForecastTimes),
    [availableListings, anchorListing, selectedForecastTimes]
  );
  const modelReads = modelRuns.map(({listing, forecasts}) => {
    const request: H5ForecastDataRequest | undefined =
      forecasts.length > 0
        ? {
            modelType: listing.modelType,
            modelId: listing.modelId,
            projectId: project.id,
            initTimes: forecasts.map((forecast) => forecast.initializationTime.toISOString()),
            validTimeStartDate: dateRange[0].toISOString(),
            validTimeEndDate: dateRange[1].toISOString(),
            columns: listing.variables.map((variable) => variable.name),
            unitSystem,
            timeAggregation,
            aggregationMethod: aggregation.aggregationMethod,
            // Never 'level': the controls do not offer it here (showLevelOption is off).
            rateVolumeMode: aggregation.rateVolumeMode as H5ForecastDataRequest['rateVolumeMode'],
            timeZone: site.localTimeZone,
          }
        : undefined;
    return {
      listing,
      forecasts,
      request,
      issueTimes: forecasts.map((forecast) => forecast.issueTime.toISOString()),
    };
  });
  const dataQueries = useH5ForecastData(site, modelReads);
  const forecastDataList = useShallowStable(dataQueries.map((query) => query.data));
  const loadingList = useShallowStable(dataQueries.map((query) => query.isLoading));
  // The zone the data was read in, which the response names whether or not the request did.
  const timeZone = forecastDataList.find(Boolean)?.timeZone ?? site.localTimeZone;

  const unsupportedSchema = listingQuery.error instanceof UnsupportedH5SchemaError;
  const showsChart = sectionListings.length > 0 && !unsupportedSchema;

  const observationDataRequest: H5ObservationDataRequest | undefined =
    showsChart && !isCumulativeMode(aggregation.rateVolumeMode)
      ? {
          projectId: project.id,
          validTimeStartDate: dateRange[0].toISOString(),
          validTimeEndDate: dateRange[1].toISOString(),
          unitSystem,
          timeAggregation,
          aggregationMethod: aggregation.aggregationMethod,
          rateVolumeMode: aggregation.rateVolumeMode as H5ObservationDataRequest['rateVolumeMode'],
          timeZone: site.localTimeZone,
        }
      : undefined;
  const observationDataQuery = useH5ObservationData(site, observationDataRequest);

  const chartModels = React.useMemo(() => {
    const extraListings = availableListings.filter((listing) => listing !== sectionDefaultListing);
    return modelRuns.map(({listing}): H5FlowModel => {
      const isDefault = listing === sectionDefaultListing;
      const name = modelNames.get(listingKey(listing))!;
      return {
        // The legacy styling identity for the default model; the others only need to differ.
        source: isDefault ? settings.source : modelSource(settings.source, name),
        name,
        columns: listing.variables.flatMap((variable) => flowColumnFor(variable) ?? []),
        deterministic: listing.variables.some((variable) => variable.statistic === 'deterministic'),
        colorIndex: isDefault ? 0 : extraListings.indexOf(listing) + 1,
      };
    });
  }, [availableListings, modelRuns, sectionDefaultListing, modelNames, settings.source]);
  const legendModels = React.useMemo(
    () =>
      modelRuns.map(({listing, forecasts, comparedTo}, index): H5LegendModel => {
        const model = {
          listing,
          name: modelNames.get(listingKey(listing))!,
          removable: canRemove(listing),
        };
        if (comparedTo && forecasts.length === 0) {
          return {
            ...model,
            noRunNote: t('noModelForecastWithinDayOf', {
              time: initTimesOf(comparedTo, locale, timeZone),
            }),
          };
        }
        const horizonDays = forecastHorizonDays(
          forecastDataList[index]?.forecasts ?? [],
          dateRange[1]
        );
        const details = [
          forecasts.length > 0 &&
            formatDateMonthDayTime(forecasts[0].initializationTime, locale, timeZone),
          horizonDays !== undefined && t('horizonDays', {count: String(horizonDays)}),
        ]
          .filter(Boolean)
          .join(' · ');
        return {
          ...model,
          ...(details && {details}),
          ...(forecasts.length > 1 && {allInitTimes: initTimesOf(forecasts, locale, timeZone)}),
          ...(loadingList[index] && {loading: true}),
        };
      }),
    [
      modelRuns,
      forecastDataList,
      loadingList,
      modelNames,
      canRemove,
      locale,
      timeZone,
      dateRange,
      t,
    ]
  );
  const observationVariants = React.useMemo(
    () =>
      (observationDataQuery.data?.variables ?? []).map((variable) => variable.observationVariant),
    [observationDataQuery.data]
  );
  const vizConfig = React.useMemo(
    () =>
      makeH5FlowVizConfig({
        models: chartModels,
        observationVariants,
        timeAggregation,
        t,
      }),
    [chartModels, observationVariants, timeAggregation, t]
  );
  const forecastTimeSeriesList = React.useMemo(
    () =>
      forecastDataList.flatMap((data, index) =>
        data && chartModels[index]
          ? [
              toForecastTimeSeries(
                data,
                {siteId: site.id, source: chartModels[index].source},
                (message) => captureMessage(message)
              ),
            ]
          : []
      ),
    [forecastDataList, chartModels, site.id]
  );
  const observationTimeSeriesList = React.useMemo(
    () =>
      observationDataQuery.data && observationDataQuery.data.validTimes.length > 0
        ? [
            toObservationTimeSeries(observationDataQuery.data, {
              siteId: site.id,
              projectId: project.id,
            }),
          ]
        : [],
    [observationDataQuery.data, site.id, project.id]
  );
  const failedMessage = (message: string, query: {error: unknown; refetch: () => unknown}) => (
    <RequestFailed message={message} error={query.error} onRetry={query.refetch} />
  );
  // With several models on the chart, a message about one names it.
  const aboutModel = (listing: H5ForecastListing, message: React.ReactNode) =>
    selectedListings.length > 1 ? (
      <>
        {nameOf(listing)}: {message}
      </>
    ) : (
      message
    );
  // A model with nothing drawn has no legend column to be removed from, so its message offers it.
  const aboutUndrawnModel = (listing: H5ForecastListing, message: React.ReactNode) => (
    <>
      {aboutModel(listing, message)} {removeButton(listing)}
    </>
  );
  const statusMessages = ((): React.ReactNode[] => {
    if (unsupportedSchema) return [t('unsupportedForecastData')];
    if (listingQuery.isError) {
      return [failedMessage(t('forecastRequestFailed'), listingQuery)];
    }
    if (listingQuery.isLoading) return [];
    // With a model to add, the legend's add button says what to do.
    if (selectedListings.length === 0) {
      return addableListings.length > 0 ? [] : [t('noSupportedFlowForecast')];
    }
    const unavailable = selectedListings
      .filter((listing) => !isAvailableListing(listing))
      .map((listing) => aboutUndrawnModel(listing, t('forecastUnavailable')));
    if (!forecastTimes) return unavailable;
    if (forecastTimes.length === 0) return [...unavailable, t('noForecastsInPeriod')];
    const failed = modelReads.flatMap(({listing}, index) => {
      const query = dataQueries[index];
      if (!query.isError) return [];
      return [
        aboutUndrawnModel(
          listing,
          query.error instanceof UnsupportedH5SchemaError
            ? t('unsupportedForecastData')
            : failedMessage(t('forecastRequestFailed'), query)
        ),
      ];
    });
    if (selectedForecastTimes.length === 0) {
      return [...unavailable, ...failed, t('noForecastsSelected')];
    }
    const withoutData = modelReads.flatMap(({listing, forecasts}, index) => {
      const data = dataQueries[index].data;
      const missing = data ? forecastTimesWithoutData(forecasts, data) : [];
      return missing.length > 0
        ? [
            aboutModel(
              listing,
              t('forecastTimesWithoutData', {
                forecasts: missing
                  .map((forecast) => formatForecastTime(forecast, timeZone))
                  .join(', '),
              })
            ),
          ]
        : [];
    });
    return [...unavailable, ...failed, ...withoutData];
  })();
  const observationStatusMessage = ((): React.ReactNode => {
    if (observationDataQuery.isError) {
      return (
        <RequestFailed
          message={t('observationRequestFailed')}
          error={observationDataQuery.error}
          onRetry={observationDataQuery.refetch}
        />
      );
    }
    if (observationDataQuery.data && observationDataQuery.data.validTimes.length === 0) {
      return t('noObservationsInPeriod');
    }
    return null;
  })();

  return (
    <div className={tw.section}>
      <div className={tw.sectionHeader}>
        <h2 className={tw.sectionTitle}>{t(section)}</h2>
        <ForecastSelect
          horizon={defaultHorizonDays}
          maxSelectedForecasts={H5_MAX_SELECTED_FORECASTS}
          site={site}
          dateRange={dateRange}
          defaultDateRange={defaultDateRange}
          setDateRange={setDateRange}
          primaryForecastTimes={forecastTimes}
          selectedPrimaryForecastTimes={selectedForecastTimes}
          setSelectedPrimaryForecastTimes={setSelectedForecastTimes}
          onNextPrimaryForecastTimesChange={onNextForecastTimesChange}
          shortcuts={{absolute: settings.shortcuts(t, defaultDateRange, defaultHorizonDays)}}
        />
      </div>
      <div className={tw.sectionBody}>
        {listingQuery.isLoading && <LoadingSpinner />}
        {(statusMessages.length > 0 || observationStatusMessage) && (
          <div className="space-y-1">
            {statusMessages.map((message, index) => (
              <p key={index}>{message}</p>
            ))}
            {observationStatusMessage && <p>{observationStatusMessage}</p>}
          </div>
        )}
        {showsChart && (
          <WithWidth>
            {(width) => (
              <Viz
                section={t(section)}
                title={t('flow')}
                site={site}
                config={vizConfig}
                renderOptions={() => (
                  <AggOptions
                    site={site}
                    agg={aggregation}
                    updateAgg={updateAggregation}
                    dateRange={dateRange}
                    showLevelOption={false}
                    analyticsSection={settings.analyticsSection}
                  />
                )}
                forecastQueries={[]}
                observationQueries={[]}
                forecastTimeSeriesList={forecastTimeSeriesList}
                observationTimeSeriesList={observationTimeSeriesList}
                loadingStateDims={[width, SOLO_VIZ_HEIGHT_WITH_LEGEND]}
                dateRange={dateRange}
                setDateRange={setDateRange}
                // Only until a model has something to draw, so adding one keeps the rest on screen.
                isLoading={
                  dataQueries.some((query) => query.isLoading) && !forecastDataList.some(Boolean)
                }
                thresholdLinesVisible={thresholdLinesVisible}
                onToggleThresholdLines={
                  thresholdConfigs.length > 0
                    ? () => setThresholdLinesVisible((visible) => !visible)
                    : undefined
                }
              >
                {(props) => (
                  <TimeSeriesViz
                    {...props}
                    timeZone={timeZone}
                    dims={[width, SOLO_VIZ_HEIGHT]}
                    renderLegend={(legend) => (
                      <H5FlowLegend
                        {...legend}
                        models={legendModels}
                        showModelInfo={showModelInfo}
                        onRemove={removeModel}
                        addable={{
                          listings: addableListings,
                          nameOf,
                          onAdd: (listing) => selectModels([...selectedListings, listing]),
                        }}
                      />
                    )}
                    renderSvg={
                      thresholdLinesVisible
                        ? ({y, graphDims}) => (
                            <ThresholdLines
                              y={y}
                              graphDims={graphDims}
                              thresholdConfigs={thresholdConfigs}
                            />
                          )
                        : undefined
                    }
                    renderHtmlOverlay={
                      thresholdLinesVisible
                        ? ({y, graphDims, margin}) => (
                            <ThresholdLabelOverlays
                              y={y}
                              graphDims={graphDims}
                              margin={margin}
                              thresholdConfigs={thresholdConfigs}
                            />
                          )
                        : undefined
                    }
                  />
                )}
              </Viz>
            )}
          </WithWidth>
        )}
        <WeatherCharts
          project={project}
          site={site}
          section={section}
          forecastTimes={forecastTimes}
          selectedForecastTimes={selectedForecastTimes}
          dateRange={dateRange}
          setDateRange={setDateRange}
          initTimeRange={initTimeRange}
          defaultTimeAggregation={settings.defaultTimeAggregation}
          analyticsSection={settings.analyticsSection}
        />
      </div>
    </div>
  );
}
